/* ============================================
   AREEZY THEME - Main JavaScript
   ============================================ */

/**
 * Add a product to cart using Shopify's AJAX API
 * @param {number} variantId - The variant ID to add
 * @param {number} quantity - The quantity to add
 * @param {string} productTitle - The product title (for feedback)
 */
function addToCart(variantId, quantity = 1, productTitle = 'Item') {
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: [{
        id: parseInt(variantId),
        quantity: parseInt(quantity)
      }]
    })
  })
  .then(response => {
    if (!response.ok) {
      throw new Error('Cart add failed');
    }
    return response.json();
  })
  .then(data => {
    // Update cart count in header
    updateCartCount();
    // Show success message
    showNotification(`${productTitle} added to cart!`, 'success');
  })
  .catch(error => {
    console.error('Error adding to cart:', error);
    showNotification('Error adding to cart. Please try again.', 'error');
  });
}

/**
 * Update the cart count badge in the header
 */
function updateCartCount() {
  fetch('/cart.js')
    .then(response => response.json())
    .then(data => {
      const cartCount = document.getElementById('cart-count');
      if (cartCount) {
        cartCount.textContent = data.item_count;
      }
    })
    .catch(error => console.error('Error updating cart count:', error));
}

/**
 * Show a notification message to the user
 * @param {string} message - The message to display
 * @param {string} type - The notification type: 'success', 'error', 'info'
 * @param {number} duration - Duration to show notification in milliseconds
 */
function showNotification(message, type = 'info', duration = 3000) {
  const notification = document.createElement('div');
  notification.className = `notification notification-${type}`;
  notification.textContent = message;

  const style = document.createElement('style');
  style.textContent = `
    .notification {
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 15px 20px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      z-index: 1000;
      animation: slideInUp 0.3s ease;
      max-width: 300px;
    }

    .notification-success {
      background-color: #10b981;
      color: white;
    }

    .notification-error {
      background-color: #ef4444;
      color: white;
    }

    .notification-info {
      background-color: #3b82f6;
      color: white;
    }

    @media (max-width: 768px) {
      .notification {
        left: 20px;
        right: 20px;
        bottom: 20px;
        max-width: none;
      }
    }
  `;

  if (!document.querySelector('style[data-notification]')) {
    style.setAttribute('data-notification', 'true');
    document.head.appendChild(style);
  }

  document.body.appendChild(notification);

  setTimeout(() => {
    notification.style.animation = 'slideOutDown 0.3s ease';
    setTimeout(() => {
      notification.remove();
    }, 300);
  }, duration);
}

/**
 * Format price with currency symbol
 * @param {number} price - The price in cents
 * @param {string} currency - Currency code (default: 'GBP')
 * @returns {string} Formatted price string
 */
function formatPrice(price, currency = 'GBP') {
  const currencies = {
    'GBP': { symbol: '£', format: (n) => `${n}` },
    'USD': { symbol: '$', format: (n) => `$${n}` },
    'EUR': { symbol: '€', format: (n) => `€${n}` },
  };

  const formatter = currencies[currency] || currencies['GBP'];
  const formatted = (price / 100).toFixed(2);
  return formatter.format(formatted);
}

/**
 * Debounce function for event handlers
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {function} Debounced function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function for event handlers
 * @param {function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {function} Throttled function
 */
function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Initialize theme functionality
 */
function initTheme() {
  // Update cart count on page load
  updateCartCount();

  // Add keyboard event listeners for accessibility
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAllModals();
    }
  });

  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href !== '#' && document.querySelector(href)) {
        e.preventDefault();
        document.querySelector(href).scrollIntoView({ behavior: 'smooth' });
      }
    });
  });

  // Lazy load images
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          imageObserver.unobserve(img);
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
}

/**
 * Close all open modals and overlays
 */
function closeAllModals() {
  document.querySelectorAll('[data-modal].open, .modal.open').forEach(modal => {
    modal.classList.remove('open');
  });
}

/**
 * Track page view for analytics
 */
function trackPageView() {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'page_view', {
      page_path: window.location.pathname,
      page_title: document.title
    });
  }
}

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid
 */
function validateEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

/**
 * Get query parameter from URL
 * @param {string} param - Parameter name
 * @returns {string|null} Parameter value or null
 */
function getQueryParam(param) {
  const params = new URLSearchParams(window.location.search);
  return params.get(param);
}

/**
 * Detect if device is mobile
 * @returns {boolean} True if mobile device
 */
function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/* ============================================
   INITIALIZATION
   ============================================ */

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTheme);
} else {
  initTheme();
}

// Track page views
document.addEventListener('DOMContentLoaded', trackPageView);
